import React, { useState } from 'react';
import { connect } from 'react-redux';
import { appendIndex, removeFromCalendar, clearCalendar, refreshUserProfileData } from '../actions/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog } from '@headlessui/react';
import { useSpring, animated } from 'react-spring';
import useMeasure from 'react-use-measure';
import toast from 'react-hot-toast';
import { getCookie } from '../utils/cookies';

const generatePastelColor = (str) => {
  // Return a default color if str is undefined or null
  if (!str) return 'hsl(210, 60%, 70%)';

  let hash = 0;
  const safeStr = String(str); // Convert to string to ensure we can use length and charCodeAt
  
  for (let i = 0; i < safeStr.length; i++) {
    hash = safeStr.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate more vibrant colors using HSL
  const h = Math.abs(hash % 360);
  const s = 60 + (Math.abs(hash) % 20); // Saturation between 60-80%
  const l = 65 + (Math.abs(hash) % 10); // Lightness between 65-75%
  return `hsl(${h}, ${s}%, ${l}%)`; // More vibrant version
};

// Create a color cache to store colors for course titles
const courseColorCache = new Map();

const ClearConfirmDialog = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Dialog Panel */}
        <div className="relative inline-block bg-gradient-to-b from-gray-800 to-gray-900 
          rounded-2xl text-left overflow-hidden shadow-xl transform transition-all 
          sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700"
        >
          <div className="px-6 pt-6 pb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6 text-red-500" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                  />
                </svg>
                Clear Schedule
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <p className="text-gray-300 text-base mb-8">
              This will remove all courses from your schedule. This action cannot be undone.
            </p>

            <div className="flex justify-end gap-4">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 
                  transition-all duration-200 flex items-center gap-2"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white 
                  rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 
                  flex items-center gap-2 shadow-lg hover:shadow-red-500/20"
              >
                Clear Schedule
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Schedule = ({ CalendarIndexes, SniperIndexes, appendIndex, removeFromCalendar, clearCalendar, currentClassesData, email, refreshUserProfileData }) => {
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [ref, bounds] = useMeasure();

  // Define time slots from 8 AM to 10 PM
  const timeSlots = [];
  for (let hour = 8; hour <= 22; hour++) {
    timeSlots.push(`${hour % 12 || 12}:00 ${hour < 12 ? 'AM' : 'PM'}`);
  }

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const timeToDecimal = (timeStr) => {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours + (minutes / 60);
  };

  const CELL_HEIGHT = 50; // Base cell height

  const calculateBlockHeight = (startTime, endTime) => {
    const start = timeToDecimal(startTime);
    const end = timeToDecimal(endTime);
    const duration = end - start;
    
    // For a 1-hour block, we want it to exactly fill one cell
    const height = Math.max(duration * CELL_HEIGHT, CELL_HEIGHT);
    
    console.log(`Block from ${startTime} to ${endTime}: duration=${duration}, height=${height}px`);
    return `${height}px`;
  };

  const calculateTopOffset = (startTime, slotTime) => {
    const start = timeToDecimal(startTime);
    const slot = Math.floor(timeToDecimal(slotTime));
    
    // Align with the grid lines
    const offset = (start - slot) * CELL_HEIGHT;
    
    console.log(`Offset for ${startTime} in slot ${slotTime}: start=${start}, slot=${slot}, offset=${offset}px`);
    return `${offset}px`;
  };

  const isBeingSniped = (courseIndex) => {
    const result = SniperIndexes?.some(item => item.sectionIndex === courseIndex);
    console.log(`Checking if ${courseIndex} is being sniped:`, result);
    console.log('Current SniperIndexes:', SniperIndexes);
    return result;
  };

  const handleSnipeClick = (e, courseIndex) => {
    e.stopPropagation();
    console.log('Snipe button clicked for index:', courseIndex);
    appendIndex([courseIndex]);
  };

  const handleRemove = (sectionIndex) => {
    console.log('Removing course with index:', sectionIndex);
    removeFromCalendar(sectionIndex);
  };

  const handleSlotMouseEnter = (slotId) => {
    setExpandedSlot(slotId);
  };

  const handleSlotMouseLeave = () => {
    if (!hoveredBlock) {
      setExpandedSlot(null);
    }
  };

  const handleBlockMouseEnter = (slotId, blockId) => {
    setExpandedSlot(slotId);
    setHoveredBlock(blockId);
  };

  const handleBlockMouseLeave = () => {
    setHoveredBlock(null);
  };

  // Schedule upload functionality
  const generateUniqueFileName = (originalName) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `schedule-${timestamp}-${randomString}.${extension}`;
  };

  const handleScheduleUpload = async () => {
    if (!selectedFile || uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing') {
      return false;
    }

    const waitingToast = toast.loading(
      <div className="flex flex-col">
        <span className="text-lg font-bold">Uploading Schedule</span>
        <span className="text-sm">Please wait while we process your updated schedule...</span>
      </div>,
      {
        position: 'top-center',
        style: {
          background: '#3B82F6',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      }
    );

    try {
      setUploadStatus('getting-url');

      // Generate unique filename
      const uniqueFileName = generateUniqueFileName(selectedFile.name);
      const s3Key = `uploads/${uniqueFileName}`;
      const contentType = selectedFile.type || 'image/png';

      const presignResponse = await fetch('https://api.ruturbo.com/process-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('session_id'),
        },
        body: JSON.stringify({
          email: email,
          action: 'generateUrl',
          key: s3Key,
          contentType: contentType
        }),
      });

      if (!presignResponse.ok) {
        throw new Error(`Failed to get URL: ${presignResponse.status}`);
      }

      const responseData = await presignResponse.json();
      const presignedUrl = responseData.url;
      
      if (!presignedUrl) {
        throw new Error('No URL returned from server');
      }

      // Upload to S3
      setUploadStatus('uploading');
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        body: selectedFile
      });

      if (!uploadResult.ok) {
        throw new Error(`S3 upload failed: ${uploadResult.status}`);
      }

      // Process the uploaded image
      setUploadStatus('processing');
      const processResponse = await fetch('https://api.ruturbo.com/process-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('session_id'),
        },
        body: JSON.stringify({
          email: email,
          action: 'extractText',
          key: s3Key,
        }),
      });

      if (!processResponse.ok) {
        throw new Error(`Processing failed: ${processResponse.status}`);
      }

      setUploadStatus('done');
      toast.dismiss(waitingToast);
      
      toast.success(
        <div className="flex flex-col">
          <span className="text-lg font-bold">Schedule Updated</span>
          <span className="text-sm">Refreshing your current classes...</span>
        </div>,
        {
          position: 'top-center',
          duration: 3000,
          style: {
            background: '#10B981',
            color: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }
      );

      // Reset file selection
      setSelectedFile(null);
      setUploadStatus('');
      
      // Refresh user data to get updated current classes
      try {
        console.log("Starting to refresh user profile data...");
        await refreshUserProfileData();
        
        toast.success(
          <div className="flex flex-col">
            <span className="text-lg font-bold">Classes Updated</span>
            <span className="text-sm">Your current classes have been refreshed successfully</span>
          </div>,
          {
            position: 'top-center',
            duration: 3000,
            style: {
              background: '#10B981',
              color: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            },
          }
        );
      } catch (error) {
        console.error('Error refreshing current classes:', error);
        toast.error('Schedule updated but failed to refresh classes. Please refresh the page.', {
          position: 'top-center',
          duration: 5000,
        });
      }

      return true;
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      toast.dismiss(waitingToast);
      
      toast.error(
        <div className="flex flex-col">
          <span className="text-lg font-bold">Upload Failed</span>
          <span className="text-sm">{error.message || 'Failed to upload schedule'}</span>
        </div>,
        {
          position: 'top-center',
          duration: 5000,
          style: {
            background: '#EF4444',
            color: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }
      );
      return false;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setUploadStatus('');
      } else {
        toast.error('Please select a valid image file', {
          position: 'top-center',
          duration: 3000,
        });
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setUploadStatus('');
    } else {
      toast.error('Please select a valid image file', {
        position: 'top-center',
        duration: 3000,
      });
    }
  };

  const handleUploadSubmit = async () => {
    if (selectedFile) {
      const success = await handleScheduleUpload();
      if (success) {
        setIsUploadModalOpen(false);
        setSelectedFile(null);
      }
    }
  };

  const handleModalClose = () => {
    if (uploadStatus !== 'getting-url' && uploadStatus !== 'uploading' && uploadStatus !== 'processing') {
      setIsUploadModalOpen(false);
      setSelectedFile(null);
      setUploadStatus('');
    }
  };

  const springProps = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 300, friction: 20 }
  });

  return (
    <animated.div style={springProps}>
      <div className="overflow-x-auto" ref={ref}>
        {/* Action Buttons */}
        <motion.div 
          className="flex justify-between items-center mb-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={() => setIsUploadModalOpen(true)}
            disabled={uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
            className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg 
              transition-all duration-200 flex items-center space-x-2 hover:shadow-lg 
              hover:shadow-blue-500/20 transform hover:-translate-y-0.5 
              ${uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing' 
                ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <motion.svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5"
              whileHover={{ rotate: 180 }}
              transition={{ duration: 0.3 }}
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </motion.svg>
            <span>
              {uploadStatus === 'getting-url' && 'Getting Upload URL...'}
              {uploadStatus === 'uploading' && 'Uploading...'}
              {uploadStatus === 'processing' && 'Processing...'}
              {(!uploadStatus || uploadStatus === 'done' || uploadStatus === 'error') && 'Import Updated Schedule'}
            </span>
          </button>
          
          <button
            onClick={() => setIsConfirmOpen(true)}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg 
              transition-all duration-200 flex items-center space-x-2 hover:shadow-lg 
              hover:shadow-red-500/20 transform hover:-translate-y-0.5"
          >
            <motion.svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5"
              whileHover={{ rotate: 180 }}
              transition={{ duration: 0.3 }}
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </motion.svg>
            <span>Clear Schedule</span>
          </button>
        </motion.div>

        {/* Clear Confirmation Dialog */}
        <ClearConfirmDialog 
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={clearCalendar}
        />

        {/* Upload Modal */}
        <AnimatePresence>
          {isUploadModalOpen && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div 
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleModalClose}
              />
              <motion.div
                className="relative bg-gray-900 rounded-xl shadow-2xl border border-gray-700 w-full max-w-md"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Import Updated Schedule
                    </h3>
                    <button
                      onClick={handleModalClose}
                      disabled={uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                      className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-50"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Instructions */}
                  <div className="mb-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      How to get your schedule screenshot:
                    </h4>
                    <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
                      <li>Login to WebReg</li>
                      <li>Navigate to the "Manage Registration" tab</li>
                      <li>Take a screenshot of your classes with the <span className="font-semibold text-blue-400">index numbers visible</span></li>
                      <li>Upload the screenshot here</li>
                    </ol>
                    <p className="text-xs text-gray-400 mt-3">
                      This helps our administrators understand your schedule and open sections during convenient times for best accuracy.
                    </p>
                    
                    {/* Example Image - Collapsible */}
                    <div className="mt-4">
                      <details className="text-sm">
                        <summary className="text-blue-400 cursor-pointer hover:text-blue-300 transition-colors">
                          View example screenshot
                        </summary>
                        <div className="mt-3 w-full max-w-md rounded-lg border border-gray-600 overflow-hidden bg-gray-800">
                          <img 
                            src="/example-schedule.png" 
                            alt="Example schedule screenshot showing course listings with index numbers" 
                            className="w-full"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden flex-col items-center justify-center p-6 text-center">
                            <svg className="w-12 h-12 text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-500 text-sm">Example image placeholder</p>
                            <p className="text-gray-600 text-xs mt-1">Shows WebReg schedule with visible index numbers</p>
                          </div>
                        </div>
                      </details>
                    </div>
                  </div>

                  {/* Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg transition-all duration-200 ${
                      isDragOver
                        ? 'border-blue-400 bg-blue-400/10'
                        : selectedFile
                        ? 'border-green-400 bg-green-400/10'
                        : 'border-gray-600 bg-gray-800/50'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="p-8 text-center">
                      {selectedFile ? (
                        // File Selected State
                        <div className="space-y-4">
                          <div className="w-16 h-16 mx-auto bg-green-400/20 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-green-400 font-medium">{selectedFile.name}</p>
                            <p className="text-gray-400 text-sm">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <button
                            onClick={() => setSelectedFile(null)}
                            disabled={uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                            className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50"
                          >
                            Remove file
                          </button>
                        </div>
                      ) : (
                        // Upload State
                        <div className="space-y-4">
                          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center transition-colors ${
                            isDragOver ? 'bg-blue-400/20' : 'bg-gray-700'
                          }`}>
                            <svg className={`w-8 h-8 transition-colors ${
                              isDragOver ? 'text-blue-400' : 'text-gray-400'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-white font-medium mb-2">
                              {isDragOver ? 'Drop your schedule image here' : 'Drag & drop your schedule image'}
                            </p>
                            <p className="text-gray-400 text-sm mb-4">or</p>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFileSelect}
                              className="hidden"
                              id="schedule-upload"
                            />
                            <label
                              htmlFor="schedule-upload"
                              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg 
                                hover:bg-blue-700 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Choose File
                            </label>
                          </div>
                          <p className="text-gray-500 text-xs">Supports: JPG, PNG, GIF (Max 10MB)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Upload Status */}
                  {uploadStatus && (
                    <div className="mt-4 p-3 rounded-lg bg-gray-800">
                      <div className="flex items-center gap-2">
                        {uploadStatus === 'getting-url' && (
                          <>
                            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-blue-400 text-sm">Getting upload URL...</span>
                          </>
                        )}
                        {uploadStatus === 'uploading' && (
                          <>
                            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-blue-400 text-sm">Uploading to server...</span>
                          </>
                        )}
                        {uploadStatus === 'processing' && (
                          <>
                            <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                            <span className="text-yellow-400 text-sm">Processing schedule...</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleModalClose}
                      disabled={uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                      className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-600 
                        hover:border-gray-500 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUploadSubmit}
                      disabled={!selectedFile || uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                        transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing' 
                        ? 'Processing...' 
                        : 'Upload Schedule'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Schedule Grid */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[800px]">
            <thead>
              <tr>
                <th className="text-gray-400 text-sm font-semibold w-20 px-2">Time</th>
                {days.map(day => (
                  <th key={day} className="text-white font-bold text-center text-sm px-2 py-2 w-1/5">{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map((timeSlot) => (
                <React.Fragment key={timeSlot}>
                  <tr>
                    <td className="text-gray-400 text-xs py-1 px-2 text-right h-[50px] border-t border-gray-700">
                      {timeSlot}
                    </td>
                    {days.map((day, dayIndex) => {
                      // Get all non-async courses for this day and time slot from CalendarIndexes
                      const calendarCoursesInSlot = CalendarIndexes
                        .filter(course => !course.isAsync)
                        .flatMap(course => {
                          const meetings = course.meetingTimes.filter(m => m.days === day);
                          return meetings.map(meeting => ({
                            ...course,
                            currentMeeting: meeting,
                            isCurrentClass: false
                          }));
                        }).filter(course => {
                          const startHour = Math.floor(timeToDecimal(course.currentMeeting.startTime));
                          const slotHour = Math.floor(timeToDecimal(timeSlot));
                          return startHour === slotHour;
                        });

                      // Get all non-async current classes for this day and time slot
                      const currentCoursesInSlot = (currentClassesData || [])
                        .filter(course => {
                          // Course is not async if it has meeting times with days and start times
                          return course?.meetingTimes && 
                                 course.meetingTimes.length > 0 && 
                                 course.meetingTimes.some(mt => mt?.days && mt?.startTime) &&
                                 !course.isAsync;
                        })
                        .flatMap(course => {
                          const meetings = course.meetingTimes.filter(m => m.days === day);
                          return meetings.map(meeting => ({
                            ...course,
                            currentMeeting: meeting,
                            isCurrentClass: true
                          }));
                        }).filter(course => {
                          const startHour = Math.floor(timeToDecimal(course.currentMeeting.startTime));
                          const slotHour = Math.floor(timeToDecimal(timeSlot));
                          return startHour === slotHour;
                        });

                      // Combine both arrays
                      const coursesInSlot = [...calendarCoursesInSlot, ...currentCoursesInSlot];

                      const hasOverlap = coursesInSlot.length > 1;
                      const slotId = `${day}-${timeSlot}`;
                      const isExpanded = expandedSlot === slotId;

                      return (
                        <td 
                          key={slotId}
                          className={`border-t border-gray-700 relative ${isExpanded ? 'h-auto' : 'h-[50px]'} w-1/5 p-0.5`}
                          onMouseEnter={() => hasOverlap && handleSlotMouseEnter(slotId)}
                          onMouseLeave={handleSlotMouseLeave}
                        >
                          <div className={`absolute inset-1 ${isExpanded ? 'h-auto' : ''}`}>
                            <div className={`relative ${isExpanded ? 'space-y-2' : ''}`}>
                              {coursesInSlot.map((course, idx) => {
                                const meeting = course.currentMeeting;
                                const blockHeight = calculateBlockHeight(meeting.startTime, meeting.endTime);
                                const heightInPixels = parseFloat(blockHeight);
                                const fontSize = heightInPixels < 35 ? '0.65rem' : '0.75rem';
                                const tooltipPosition = dayIndex <= 1 ? 'right' : 'left';
                                const blockId = `${slotId}-${course.sectionIndex}`;
                                const isHovered = hoveredBlock === blockId;
                                const totalBlocks = coursesInSlot.length;
                                const overlapOffset = heightInPixels * 0.7; // Show 70% of each block

                                return (
                                  <motion.div
                                    key={course.sectionIndex}
                                    className="absolute w-full"
                                    initial={false}
                                    animate={{
                                      y: isExpanded ? idx * overlapOffset : 0,
                                      opacity: isExpanded ? 1 : totalBlocks > 1 ? 1 - (idx * 0.15) : 1,
                                      scale: isExpanded ? 1 : totalBlocks > 1 ? 1 - (idx * 0.05) : 1,
                                      zIndex: isExpanded ? (totalBlocks - idx) * 10 : isHovered ? 60 : totalBlocks - idx
                                    }}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    onMouseEnter={() => {
                                      setHoveredBlock(blockId);
                                      if (hasOverlap) handleSlotMouseEnter(slotId);
                                    }}
                                    onMouseLeave={() => {
                                      setHoveredBlock(null);
                                      if (hasOverlap) handleSlotMouseLeave();
                                    }}
                                  >
                                    <div
                                      className={`rounded-md shadow-md overflow-hidden transition-all duration-200 
                                        ${course.isCurrentClass ? '' : 'hover:scale-[1.02] hover:shadow-lg'} mx-auto w-[98%] absolute ${
                                          isExpanded && !course.isCurrentClass ? 'hover:translate-y-[-8px]' : ''
                                        }`}
                                      style={{
                                        height: blockHeight,
                                        top: calculateTopOffset(meeting.startTime, timeSlot),
                                        background: course.isCurrentClass
                                          ? 'linear-gradient(to right, #ffffff, #f8f9fa)'
                                          : isBeingSniped(course.sectionIndex)
                                          ? 'linear-gradient(to right, rgb(22 163 74), rgb(34 197 94))'
                                          : `linear-gradient(to right, ${
                                              courseColorCache.get(course.courseTitle) || (() => {
                                                const baseColor = generatePastelColor(course.courseTitle);
                                                courseColorCache.set(course.courseTitle, baseColor);
                                                return baseColor;
                                              })()
                                            }, ${
                                              courseColorCache.get(course.courseTitle) || (() => {
                                                const baseColor = generatePastelColor(course.courseTitle);
                                                courseColorCache.set(course.courseTitle, baseColor);
                                                return baseColor;
                                              })()
                                            })`
                                      }}
                                    >
                                      <div className={`p-0.5 h-full flex flex-col justify-between
                                        ${course.isCurrentClass 
                                          ? 'text-gray-700' 
                                          : isBeingSniped(course.sectionIndex) 
                                          ? 'text-white' 
                                          : 'text-gray-900'}`}
                                      >
                                        <div style={{ fontSize }}>
                                          <div className="font-bold leading-tight truncate">{course.courseTitle}</div>
                                          <div className="leading-tight truncate">{course.professor}</div>
                                          <div className="leading-tight text-[0.65rem] truncate">
                                            {`${meeting.startTime} - ${meeting.endTime}`}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Tooltip */}
                                    {isHovered && (
                                      <div className={`absolute 
                                        ${tooltipPosition === 'right' 
                                          ? 'left-[102%]' 
                                          : 'right-[102%]'} 
                                        top-0 bg-gray-900 text-white rounded-lg shadow-xl p-4 z-[100] w-80 
                                        border border-gray-700 transform transition-transform duration-200
                                        hover:scale-[1.02]`}
                                      >
                                        <div className={`absolute top-4 
                                          ${tooltipPosition === 'right'
                                            ? 'right-full border-r-gray-900'
                                            : 'left-full border-l-gray-900'}
                                          border-8 border-transparent`}
                                        />
                                        
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-start gap-2">
                                            <h4 className="font-bold text-lg flex-1 break-words">{course.courseTitle}</h4>
                                            {!course.isCurrentClass && (
                                              <button
                                                onClick={() => handleRemove(course.sectionIndex)}
                                                className="text-red-500 hover:text-red-400 p-1 rounded-full 
                                                  hover:bg-red-500/10 transition-colors flex-shrink-0"
                                                type="button"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              </button>
                                            )}
                                            {course.isCurrentClass && (
                                              <div className="text-green-500 p-1 flex-shrink-0">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                          </div>
                                          <div className="space-y-1.5 text-sm">
                                            <p><span className="font-semibold text-gray-300">Professor:</span> <span className="text-white">{course.professor}</span></p>
                                            <p><span className="font-semibold text-gray-300">Section Number:</span> <span className="text-white">{course.sectionNumber}</span></p>
                                            <p><span className="font-semibold text-gray-300">Index:</span> <span className="text-white">{course.sectionIndex}</span></p>
                                            <p><span className="font-semibold text-gray-300">Time:</span> <span className="text-white">{`${meeting.startTime} - ${meeting.endTime}`}</span></p>
                                            <p><span className="font-semibold text-gray-300">Day:</span> <span className="text-white">{meeting.days}</span></p>
                                          </div>
                                          
                                          {course.isCurrentClass ? (
                                            <div className="mt-3 bg-gray-500 text-white px-4 py-2 rounded-md 
                                              text-sm font-medium text-center cursor-default">
                                              Currently Enrolled ✓
                                            </div>
                                          ) : isBeingSniped(course.sectionIndex) ? (
                                            <div className="mt-3 bg-green-500 text-white px-4 py-2 rounded-md 
                                              text-sm font-medium text-center cursor-default">
                                              Added to Snipe List ✓
                                            </div>
                                          ) : (
                                            <button
                                              onClick={(e) => handleSnipeClick(e, course.sectionIndex)}
                                              className="mt-3 w-full bg-blue-600 text-white px-4 py-2 rounded-md 
                                                hover:bg-blue-700 transition-colors text-sm font-semibold"
                                            >
                                              Add to Snipe List
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </motion.div>
                                );
                              })}
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Async Classes Section */}
        {(() => {
          // Helper function to determine if a course is async
          const isAsyncCourse = (course) => {
            return course?.isAsync || 
                   !course?.meetingTimes || 
                   course?.meetingTimes?.length === 0 ||
                   course?.meetingTimes?.every(mt => !mt?.days || !mt?.startTime);
          };

          const asyncCalendarCourses = CalendarIndexes?.filter(isAsyncCourse) || [];
          const asyncCurrentCourses = currentClassesData?.filter(isAsyncCourse) || [];
          
          return (asyncCalendarCourses.length > 0 || asyncCurrentCourses.length > 0);
        })() && (
          <motion.div
            className="mt-8 border-t border-gray-700 pt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Asynchronous Courses
            </h3>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {(() => {
                // Helper function to determine if a course is async
                const isAsyncCourse = (course) => {
                  return course?.isAsync || 
                         !course?.meetingTimes || 
                         course?.meetingTimes?.length === 0 ||
                         course?.meetingTimes?.every(mt => !mt?.days || !mt?.startTime);
                };

                return [
                  ...(CalendarIndexes?.filter(isAsyncCourse).map(course => ({ ...course, isCurrentClass: false })) || []),
                  ...(currentClassesData?.filter(isAsyncCourse).map(course => ({ ...course, isCurrentClass: true })) || [])
                ];
              })()
                .map((course) => {
                  if (!course) return null;

                  const isSnipedClass = isBeingSniped(course.sectionIndex);
                  const courseString = course.courseNumber || course.courseString || 'Unknown Course';
                  const bgColor = course.isCurrentClass 
                    ? '#ffffff' 
                    : courseColorCache.get(courseString) || generatePastelColor(courseString);
                  if (!course.isCurrentClass) {
                    courseColorCache.set(courseString, bgColor);
                  }

                  return (
                    <motion.div
                      key={course.sectionIndex}
                      className={`rounded-lg overflow-hidden border transition-all duration-200 ${
                        course.isCurrentClass 
                          ? 'bg-white/90 border-gray-300 text-gray-800' 
                          : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
                      }`}
                      style={course.isCurrentClass ? {} : {
                        borderColor: `${bgColor}20`,
                        backgroundColor: `${bgColor}10`
                      }}
                      whileHover={{ scale: course.isCurrentClass ? 1 : 1.02 }}
                      layout
                    >
                      <div className="p-4 space-y-3">
                        {/* Course Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-sm font-mono ${
                                course.isCurrentClass ? 'text-gray-600' : 'text-gray-400'
                              }`}>
                                {courseString}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                course.isCurrentClass 
                                  ? 'bg-gray-200 text-gray-700' 
                                  : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                {course.isCurrentClass ? 'Enrolled' : 'Async'}
                              </span>
                            </div>
                            <h4 className={`font-medium ${
                              course.isCurrentClass ? 'text-gray-800' : 'text-white'
                            }`}>{course.courseTitle || course.title || 'Untitled Course'}</h4>
                          </div>
                        </div>

                        {/* Course Details */}
                        <div className={`text-sm space-y-1 ${
                          course.isCurrentClass ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>
                              {course.professor ? (
                                <>Professor: {course.professor}</>
                              ) : (
                                <>Section {course.sectionNumber || 'N/A'}</>
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <span>Index: {course.sectionIndex}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 pt-2">
                          {course.isCurrentClass ? (
                            <div className="flex-1 px-3 py-1.5 bg-gray-500 text-white rounded-lg 
                              text-sm font-medium text-center cursor-default transition-colors duration-200">
                              Currently Enrolled ✓
                            </div>
                          ) : !isSnipedClass ? (
                            <button
                              onClick={(e) => handleSnipeClick(e, course.sectionIndex)}
                              className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg 
                                hover:bg-blue-600 transition-colors duration-200 text-sm font-medium"
                            >
                              Add to Snipe List
                            </button>
                          ) : (
                            <div className="flex-1 px-3 py-1.5 bg-green-500 text-white rounded-lg 
                              text-sm font-medium text-center cursor-default transition-colors duration-200">
                              Added to Snipe List ✓
                            </div>
                          )}
                          {!course.isCurrentClass && (
                            <button
                              onClick={() => handleRemove(course.sectionIndex)}
                              className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg 
                                hover:bg-red-500/20 transition-colors duration-200 text-sm font-medium"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
      </div>
    </animated.div>
  );
};

const mapStateToProps = (state) => ({
  CalendarIndexes: state.index.CalendarIndexes,
  SniperIndexes: state.index.SniperIndexes,
  currentClassesData: state.profile.currentClassesData,
  email: state.profile.email
});

export default connect(mapStateToProps, { 
  appendIndex, 
  removeFromCalendar,
  clearCalendar,
  refreshUserProfileData
})(Schedule);
